/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

/**
 * Calculates the present value factor for a pension product based on the account object and the current date.
 * It returns "By what factor do we need to multiply the expected payout to get its present value?"
 * PV of the future value of a stream of payments is calculated using daily compounding, thereafter the FV is calculated using the payout frequency.
 * @param {Account object} pension account object (uses properties: payoutsPhaseStart, payoutsFrequency, payoutsDuration)
 * @param {int} currentDate which needs to be taken as basis of calculation (usually baselineView.current)
 * @param {float} discountRate (must be determined by the caller)
 * @param {boolean} lumpSumMode - if true, calculate PV for lump sum payout at the start of the payout phase
 * @returns {float} present value factor that the monthly payout should be multiplied by to return the present value of the pension product
 */
export default function calculatePVFactor(account, currentDate, discountRate, lumpSumMode = false) {
  // Destructuring relevant properties from the account object
  const { payoutsPhaseStart, payoutsFrequency, payoutsDuration } = account;
  // if (account.id === 'pH3zXbopZxLuAjNoqgO1Q') console.info('DEBUG calculatePVFactor', account, currentDate, discountRate, lumpSumMode, payoutsPhaseStart, payoutsFrequency, payoutsDuration);

  // Calculate the annual discount rate (assuming discountRate is provided as an annual percentage)
  const dayDiscountRate = discountRate / 100 / 365.25;
  const periodDiscountRate = discountRate / 100 / (12 / payoutsFrequency);

  // Calculate the number of periods from the current date to the start of the payout phase
  const daysUntilPayoutPhase = Math.max(0, dayjs.utc(payoutsPhaseStart).diff(dayjs.utc(currentDate), 'day'));
  // console.log('daysUntilPayoutPhase', daysUntilPayoutPhase, 'current date', currentDate, 'payouts start date', payoutsPhaseStart);

  // number of complete payout periods before the end of the payouts phase
  const totalPayoutPeriods = payoutsDuration > 0 ? Math.floor(payoutsDuration / payoutsFrequency) : Number.POSITIVE_INFINITY;

  // Calculate Present Value at the time of first payout
  let pvFactor;
  if (lumpSumMode) pvFactor = 1; // lump sum IS the Present Value at the time of first payout
  else if (totalPayoutPeriods === Number.POSITIVE_INFINITY) {
    pvFactor = periodDiscountRate > 0 ? 1 / periodDiscountRate : 0;
  } else {
    // Annuity for a given time period formula (expected monthly payment x this is the present value)
    pvFactor = (1 - (1 + periodDiscountRate) ** -totalPayoutPeriods) / periodDiscountRate;
  }
  // console.log('future PV for 100€', account.name, pvFactor * 100);

  // Applying discounting for the time period until the first payment (if any)
  if (daysUntilPayoutPhase > 0) {
    pvFactor /= (1 + dayDiscountRate) ** daysUntilPayoutPhase;
  }

  return pvFactor;
}
